import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import './ThreeImageTiltSimple.css';

// Componente de fallback quando 3D não está disponível
const FallbackMessage = ({ onSwitch }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onSwitch();
    }, 2000);

    return () => clearTimeout(timer);
  }, [onSwitch]);

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      color: '#6c757d',
      flexDirection: 'column'
    }}>
      <div>⚠️ Visualizador 3D não disponível</div>
      <small>Mudando para modo imagem...</small>
    </div>
  );
};

// Componente alternativo sem Three.js para evitar conflitos
const ThreeImageTiltSimple = ({ alt, loading = false, onLoad, onError, onHotspotClick, sensores = [], sensorSelecionado, equipamentos = [], equipamentoSelecionado, isFullscreen = false, medicoes = [] }) => {

  const containerRef = useRef();
  const modelViewerRef = useRef();
  // Estados existentes
  const [isLoaded, setIsLoaded] = useState(false);
  const [hoveredPosition, setHoveredPosition] = useState(null);
  const [localSelectedPosition, setLocalSelectedPosition] = useState(null);
  const [modelVariant, setModelVariant] = useState('frente');
  // Novo: camada selecionada para garantir apenas 1 hotspot selecionado por vez (fixa na base)
  const [selectedLayer, setSelectedLayer] = useState('base'); // sempre 'base'
  const rafIdRef = useRef(null);

  // URL alvo do QR para abrir o visualizador 3D nativo
  const target3DUrl = useMemo(() => {
    try {
      const origin = typeof window !== 'undefined' && window.location ? window.location.origin : '';
      return `${origin}/3d/3d.html`;
    } catch (_) {
      return `/3d/3d.html`;
    }
  }, []);
  // Geração do QR usando serviço leve (sem dependências). Mantemos sutil e com margem pequena.
  const qrImageUrl = useMemo(() => {
    const base = 'https://api.qrserver.com/v1/create-qr-code/';
    const size = isFullscreen ? '110x110' : '90x90';
    const params = new URLSearchParams({ size, data: target3DUrl, margin: '2' });
    return `${base}?${params.toString()}`;
  }, [target3DUrl, isFullscreen]);

  // Função para obter sensores de um equipamento específico
  const getSensoresDoEquipamento = useCallback((equipamento) => {
    if (!medicoes.length || !equipamento) return [];

    const medicao = medicoes.find(m => m.serialnumber === equipamento.serialnumber);
    if (medicao && Array.isArray(medicao.sensores)) {
      return medicao.sensores
        .filter(sensor => sensor.ta !== -99 && sensor.tg !== -99 && sensor.ur !== -99) // Filtrar sensores que não existem
        .map((s, idx) => ({
          ...s,
          id: idx + 1,
          status: (s.ta === -88 || s.tg === -88 || s.ur === -88) ? 'falha' : 'normal'
        }));
    }
    return [];
  }, [medicoes]);

  // Função para retornar o src do modelo 3D
  const getDisplaySrc = useCallback(() => {
    // Mapear a variante para o arquivo do modelo existente em /public/3d
    // Variante 'traseira' deve usar explicitamente 'secador-traseira.glb'
    if (modelVariant === 'traseira') return '/3d/secador-traseira.glb';
    return '/3d/secador.glb';
  }, [modelVariant]);

  // Atualizar isLoaded quando necessário
  useEffect(() => {
    // Sempre que a variante mudar, reiniciamos o estado de carregamento
    setIsLoaded(false);
    // Limpar seleção local ao trocar de modelo (frente/traseira)
    setLocalSelectedPosition(null);
  }, [modelVariant]);

  const handleImageError = useCallback((error) => {
    // console.debug('Erro ao carregar modelo 3D:', getDisplaySrc(), error);
    if (onError) onError(error);
  }, [onError]);

  // Gerenciar eventos do model-viewer
  useEffect(() => {
    const modelViewer = modelViewerRef.current;
    if (!modelViewer) return;

    const handleLoad = () => {
      // console.debug('Model 3D carregado com sucesso');
      setIsLoaded(true);
      if (onLoad) onLoad();
    };

    const handleError = (error) => {
      // console.debug('Erro ao carregar model 3D:', error);
      handleImageError(error);
    };

    modelViewer.addEventListener('load', handleLoad);
    modelViewer.addEventListener('error', handleError);

    return () => {
      modelViewer.removeEventListener('load', handleLoad);
      modelViewer.removeEventListener('error', handleError);
    };
  }, [onLoad, handleImageError]);

  // Normalizar posição selecionada para evitar duplicidade (prioriza o pai)
  const selectedPosition = useMemo(() => {
    if (equipamentoSelecionado && equipamentoSelecionado.position != null) {
      return Number(equipamentoSelecionado.position);
    }
    if (localSelectedPosition != null) return Number(localSelectedPosition);
    return null;
  }, [equipamentoSelecionado, localSelectedPosition]);

  // Função para atualizar classes dos hotspots
  // Abordagem simplificada: usar equipamento selecionado do pai diretamente
  const updateHotspotClasses = useCallback(() => {
    const modelViewer = modelViewerRef.current;
    if (!modelViewer || !isLoaded) return;

    const hotspots = modelViewer.querySelectorAll('.Hotspot');
    // console.debug('Atualizando classes dos hotspots:', hotspots.length);

    hotspots.forEach((hotspot) => {
      const posicao = Number(hotspot.getAttribute('data-position-number'));
      if (selectedPosition != null && posicao === selectedPosition) {
        hotspot.classList.add('selected');
      } else {
        hotspot.classList.remove('selected');
      }
    });
  }, [isLoaded, selectedPosition]);

  // Atualizar classes dos hotspots quando o estado muda
  useEffect(() => {
    if (!isLoaded) return;
    cancelAnimationFrame(rafIdRef.current);
    rafIdRef.current = requestAnimationFrame(() => {
      updateHotspotClasses();
    });
    return () => cancelAnimationFrame(rafIdRef.current);
  }, [updateHotspotClasses, isLoaded, equipamentoSelecionado]);
  // Removido o listener global de clique; agora o clique é tratado diretamente em cada hotspot

  // Sincronizar seleção local quando o pai fornecer um equipamentoSelecionado
  useEffect(() => {
    if (!equipamentoSelecionado) return;
    const eq = equipamentos.find(e => e._id === equipamentoSelecionado._id);
    if (eq && eq.position != null) {
      setLocalSelectedPosition(Number(eq.position));
      // Sempre destacar a camada base, como antes
      setSelectedLayer('base');
    }
  }, [equipamentoSelecionado, equipamentos]);

  // (Removido) Card fixo no topo — substituído por janelas nos hotspots

  // Gerar hotspots dinamicamente baseados nos equipamentos - memo para evitar re-renders
  const hotspots = useMemo(() => {
    if (!equipamentos || equipamentos.length === 0) {
      return [];
    }

    // Posições fixas para os 20 hotspots no modelo 3D
    const positions = [
      { position: "-2.050513171551436m 0.23889068752489584m 48.39620440939026m", normal: "0.9919013905416886m -0.000010420956016040915m -0.1270103591557328m" },
      { position: "-3.051323959270878m 0.25442133620566165m 48.38987119912374m", normal: "0.9919013905416886m -0.000010420956016040915m -0.1270103591557328m" },
      { position: "-4.05213857537222m 0.250449543144905m 48.38350969082502m", normal: "0.9919013905416886m -0.000010420956016040915m -0.1270103591557328m" },
      { position: "-5.051793333061957m 0.22683079620662647m 48.38620974114575m", normal: "0.9919013905415885m -0.000010430646182326965m -0.12701035915572m" },
      { position: "-6.051440575576555m 0.24222384051473636m 48.38896337544159m", normal: "0.9919013905415885m -0.000010430646182326965m -0.12701035915572m" },
      { position: "-2.0503935540916647m 0.24064772354145134m 47.406664653320476m", normal: "0.9990986094400777m -0.000010985805861034413m 0.04244959945883456m" },
      { position: "-3.0519960276748317m 0.23690788880692346m 47.384624091659084m", normal: "0.9919013905433763m -0.000010256385955108827m -0.1270103591559489m" },
      { position: "-4.050138354580159m 0.2420131481804474m 47.39913138206056m", normal: "0.9919013905433763m -0.000010256385955108827m -0.1270103591559489m" },
      { position: "-5.050308881223937m 0.22740741965217787m 47.404664365484976m", normal: "0.9990992198301172m -0.000010997955429901551m 0.04243523080998135m" },
      { position: "-6.051080380536078m 0.233179595286907m 47.39177714407166m", normal: "0.9919013905432792m -0.00001026592309212833m -0.12701035915593648m" },
      { position: "-2.0501801429403717m 0.2491303639478556m 46.401644762672966m", normal: "0.9990986094324209m -0.000011662547157278803m 0.04244959945850924m" },
      { position: "-3.053132108270518m 0.22538143739227054m 46.37978761955925m", normal: "0.9561690370601933m -0.000012430194200133535m -0.2928152530399894m" },
      { position: "-4.054224018463996m 0.23048698438142673m 46.37622184158027m", normal: "0.9561690370601933m -0.000012430194200133535m -0.2928152530399894m" },
      { position: "-5.052604975907067m 0.24580133919760555m 46.38150875027893m", normal: "0.956177096407268m -0.000012430333015790136m -0.29278893447610554m" },
      { position: "-6.053696943047849m 0.2390473227698949m 46.37794293923752m", normal: "0.956177096407268m -0.000012430333015790136m -0.29278893447610554m" },
      { position: "-2.0506123416112745m 0.2297053742103817m 45.39543046140878m", normal: "0.991901390531425m -0.000011370635612644697m -0.1270103591544186m" },
      { position: "-3.051077903138481m 0.24172798018940922m 45.39179353105164m", normal: "0.991901390531425m -0.000011370635612644697m -0.1270103591544186m" },
      { position: "-4.05154536064859m 0.24395371083869397m 45.38814267087493m", normal: "0.991901390531425m -0.000011370635612644697m -0.1270103591544186m" },
      { position: "-5.057120439925449m 0.24534644511641818m 45.36676254347714m", normal: "0.956177096420136m -0.000011295900634725992m -0.2927889344800458m" },
      { position: "-6.052794769965134m 0.2482838601637068m 45.38088901095899m", normal: "0.956177096420136m -0.000011295900634725992m -0.2927889344800458m" }
    ];

    const positionsTraseira = [
      { position: "-2246.340656641667m 234.91203844195115m 48379.812862550636m", normal: "-0.9709495092111421m 0.000001623478847491242m -0.23928445532463394m" },
      { position: "-3249.2709970146752m 200.57482998809792m 48398.48912871984m", normal: "-0.9999999999987167m -0.000001602074162433572m 0m" },
      { position: "-4248.048055683841m 213.57739384409797m 48386.74107638013m", normal: "-0.9709471565946902m 0.0000016236094406539115m -0.23929400138334014m" },
      { position: "-5242.3540397049655m 200m 48362.318004094865m", normal: "0m 1m 0m" },
      { position: "-6242.603898743066m 205.6951743172358m 48366.58293824506m", normal: "-0.8854379156736498m -0.0000018868872450434275m -0.46475767608931695m" },
      { position: "-2238.1873633333817m 203.92465515283038m 47449.34123518011m", normal: "-0.8854687438908404m -0.000009269835538334025m 0.4646989385682385m" },
      { position: "-3231.145523452673m 208.49686677013233m 47461.863741403606m", normal: "-0.7484817456675662m -0.000007441641340740383m 0.6631553938158499m" },
      { position: "-4244.789437527346m 208.05533662184462m 47433.989921323555m", normal: "-0.9709378772284012m -0.00000647518046803563m 0.23933164964391665m" },
      { position: "-5263.870229208245m 199.99999999999636m 47446.92363909315m", normal: "0m 1m 0m" },
      { position: "-6248.648657854719m 214.92484672138016m 47418.33374414568m", normal: "-0.9709378772284012m -0.00000647518046803563m 0.23933164964391665m" },
      { position: "-2246.5047595296055m 215.6225396755617m 46427.032891694966m", normal: "-0.9709402303074774m -0.000023825400634919255m 0.23932210220289551m" },
      { position: "-3245.015940056961m 214.87445011692398m 46433.07302290229m", normal: "-0.9709402303074774m -0.000023825400634919255m 0.23932210220289551m" },
      { position: "-4253.027183568571m 200m 46447.2469480331m", normal: "0m 1m 0m" },
      { position: "-5243.512132701639m 200m 46448.09135179259m", normal: "0m 1m 0m" },
      { position: "-6248.714324303692m 206.28397262559884m 46418.06756562354m", normal: "-0.9709378769731033m -0.000023828691573179094m 0.2393316495809869m" },
      { position: "-2260.6268306010097m 200m 45432.12949519832m", normal: "0m 1m 0m" },
      { position: "-3249.7749254543523m 200m 45429.320208166966m", normal: "0m 1m 0m" },
      { position: "-4238.6792221283395m 220.31305238376808m 45448.40459675193m", normal: "-0.8854729105656146m -0.000021178842713908526m 0.4646909986280298m" },
      { position: "-5240.593578165652m 199.99999999999636m 45445.769037642254m", normal: "0m 1m 0m" },
      { position: "-6245.206572214454m 212.48799444848555m 45432.298494358096m", normal: "-0.9709378770510355m -0.000020181101534809195m 0.23933164960019684m" }
    ];

    const equipamentosPorPosicao = {};
    equipamentos.forEach(eq => {
      equipamentosPorPosicao[Number(eq.position)] = eq;
    });

    const offsetPositionY = (positionStr, dy) => {
      try {
        const parts = positionStr.split(' ');
        if (parts.length !== 3) return positionStr;
        const x = parseFloat(parts[0].replace('m', ''));
        const y = parseFloat(parts[1].replace('m', ''));
        const z = parseFloat(parts[2].replace('m', ''));
        const fy = Number.isFinite(y) ? y + dy : y;
        return `${x}m ${fy}m ${z}m`;
      } catch (_) {
        return positionStr;
      }
    };

    // Seleciona o conjunto de posições com base na variante do modelo
    const basePositions = modelVariant === 'traseira' ? positionsTraseira : positions;

    // Remapeamento opcional das posições para a variante traseira.
    // Caso a ordem do array positionsTraseira não corresponda 1:1 às posições físicas (1..20),
    // ajuste o array abaixo com os índices corretos (base 0) para cada posição.
    // Exemplo de remapeamento (identidade por padrão): [0,1,2,...,19]
    const remapTraseira = [
      0, 1, 2, 3, 4,
      5, 6, 7, 8, 9,
      10, 11, 12, 13, 14,
      15, 16, 17, 18, 19
    ];

    const activePositions = basePositions.map((p, i) => {
      if (modelVariant !== 'traseira') return p;
      const remappedIndex = remapTraseira[i] ?? i;
      return positionsTraseira[remappedIndex] || p;
    });

    const elements = [];

    // Deltas de altura por variante para separar as camadas visualmente
    const yDeltas = modelVariant === 'traseira'
      ? { d1: 6000, d2: 12000, d3: 18000 }
      : { d1: 6, d2: 12, d3: 18 };

  // (removido) baseYOffset - não utilizado na lógica atual

    activePositions.forEach((pos, index) => {
      const posicao = index + 1;
      const equipamento = equipamentosPorPosicao[posicao];
      if (!equipamento) return;

      const initialClass = 'Hotspot';
      // Leituras de sensores para o equipamento selecionado, por índice
      const sensoresEquip = getSensoresDoEquipamento(equipamento)
        .filter(s => s.ta !== -99 && s.tg !== -99 && s.ur !== -99);
      const getInfoForLayer = (layerIndex) => {
        const sensor = sensoresEquip[layerIndex - 1]; // 1->S1, 2->S2, etc.
        const baseText = `S${layerIndex}: `;
        if (!sensor) {
          return { text: `${baseText}—`, borderColor: '#888', title: `Sensor S${layerIndex} sem dados` };
        }
        const temFalha = sensor.ta === -88 || sensor.tg === -88 || sensor.ur === -88;
        let borderColor = '#69db7c';
        if (temFalha) borderColor = '#dc3545';
        else if (Number.isFinite(sensor.ta)) {
          const t = sensor.ta;
          if (t > 90) borderColor = '#ff6b6b';
          else if (t > 60) borderColor = '#ffd43b';
          else if (t < 40) borderColor = '#4dabf7';
        }
        const text = temFalha
          ? `${baseText}Falha`
          : `${baseText}TA ${sensor.ta}° | TG ${sensor.tg}° | UR ${sensor.ur}%`;
        const title = temFalha ? `Falha no sensor S${layerIndex}` : `Leituras do sensor S${layerIndex}`;
        return { text, borderColor, title };
      };

      // (removido) log de clique de depuração

      // Base
      elements.push(
        <button
          key={`hotspot-base-${posicao}-${equipamento._id}`}
          className={`${initialClass} base-layer ${selectedPosition === posicao ? 'selected' : ''} ${hoveredPosition === posicao ? 'hovered' : ''}`}
          slot={`hotspot-${posicao}`}
          data-position={pos.position}
          data-normal={pos.normal}
          data-visibility-attribute="visible"
          data-equipment-id={equipamento._id}
          data-position-number={posicao}
          data-layer="base"
          style={{ pointerEvents: 'auto' }}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            setSelectedLayer('base');
            setLocalSelectedPosition(posicao);
            onHotspotClick && onHotspotClick(equipamento);
          }}
          onMouseEnter={() => setHoveredPosition(posicao)}
          onMouseLeave={() => setHoveredPosition(null)}
        >
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <div className="HotspotAnnotation">{posicao}</div>
            {((selectedPosition === posicao) || hoveredPosition === posicao) && (() => {
              const info = getInfoForLayer(1); return (
                <div
                  className="HotspotInfo"
                  style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: 0,
                    transform: 'translateY(-4px)',
                    background: 'rgba(0,0,0,0.55)',
                    color: '#fff',
                    border: `1px solid ${info.borderColor}`,
                    borderRadius: 4,
                    padding: '2px 4px',
                    fontSize: 8,
                    lineHeight: 1.1,
                    whiteSpace: 'nowrap',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                    pointerEvents: 'none',
                  }}
                  title={info.title}
                >
                  <span>{info.text}</span>
                </div>
              );
            })()}
          </div>
        </button>
      );

  const upperYDelta = yDeltas.d1;
      elements.push(
        <button
          key={`hotspot-upper-${posicao}-${equipamento._id}`}
          className={`${initialClass} upper-layer ${selectedPosition === posicao ? 'selected' : ''} ${hoveredPosition === posicao ? 'hovered' : ''}`}
          slot={`hotspot-upper-${posicao}`}
          data-position={offsetPositionY(pos.position, upperYDelta)}
          data-normal={pos.normal}
          data-visibility-attribute="visible"
          data-equipment-id={equipamento._id}
          data-position-number={posicao}
          data-layer="upper"
          style={{ pointerEvents: 'auto' }}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            setSelectedLayer('base');
            setLocalSelectedPosition(posicao);
            onHotspotClick && onHotspotClick(equipamento);
          }}
          onMouseEnter={() => setHoveredPosition(posicao)}
          onMouseLeave={() => setHoveredPosition(null)}
        >
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <div className="HotspotAnnotation">{posicao}</div>
            {((selectedPosition === posicao) || hoveredPosition === posicao) && (() => {
              const info = getInfoForLayer(2); return (
                <div
                  className="HotspotInfo"
                  style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: 0,
                    transform: 'translateY(-4px)',
                    background: 'rgba(0,0,0,0.55)',
                    color: '#fff',
                    border: `1px solid ${info.borderColor}`,
                    borderRadius: 4,
                    padding: '2px 4px',
                    fontSize: 8,
                    lineHeight: 1.1,
                    whiteSpace: 'nowrap',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                    pointerEvents: 'none',
                  }}
                  title={info.title}
                >
                  <span>{info.text}</span>
                </div>
              );
            })()}
          </div>
        </button>
      );

  const upper2YDelta = yDeltas.d2;
      elements.push(
        <button
          key={`hotspot-upper2-${posicao}-${equipamento._id}`}
          className={`${initialClass} upper2-layer ${selectedPosition === posicao ? 'selected' : ''} ${hoveredPosition === posicao ? 'hovered' : ''}`}
          slot={`hotspot-upper2-${posicao}`}
          data-position={offsetPositionY(pos.position, upper2YDelta)}
          data-normal={pos.normal}
          data-visibility-attribute="visible"
          data-equipment-id={equipamento._id}
          data-position-number={posicao}
          data-layer="upper2"
          style={{ pointerEvents: 'auto' }}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            setSelectedLayer('base');
            setLocalSelectedPosition(posicao);
            onHotspotClick && onHotspotClick(equipamento);
          }}
          onMouseEnter={() => setHoveredPosition(posicao)}
          onMouseLeave={() => setHoveredPosition(null)}
        >
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <div className="HotspotAnnotation">{posicao}</div>
            {((selectedPosition === posicao) || hoveredPosition === posicao) && (() => {
              const info = getInfoForLayer(3); return (
                <div
                  className="HotspotInfo"
                  style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: 0,
                    transform: 'translateY(-4px)',
                    background: 'rgba(0,0,0,0.55)',
                    color: '#fff',
                    border: `1px solid ${info.borderColor}`,
                    borderRadius: 4,
                    padding: '2px 4px',
                    fontSize: 8,
                    lineHeight: 1.1,
                    whiteSpace: 'nowrap',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                    pointerEvents: 'none',
                  }}
                  title={info.title}
                >
                  <span>{info.text}</span>
                </div>
              );
            })()}
          </div>
        </button>
      );

  const upper3YDelta = yDeltas.d3;
      elements.push(
        <button
          key={`hotspot-upper3-${posicao}-${equipamento._id}`}
          className={`${initialClass} upper3-layer ${selectedPosition === posicao ? 'selected' : ''} ${hoveredPosition === posicao ? 'hovered' : ''}`}
          slot={`hotspot-upper3-${posicao}`}
          data-position={offsetPositionY(pos.position, upper3YDelta)}
          data-normal={pos.normal}
          data-visibility-attribute="visible"
          data-equipment-id={equipamento._id}
          data-position-number={posicao}
          data-layer="upper3"
          style={{ pointerEvents: 'auto' }}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            setSelectedLayer('base');
            setLocalSelectedPosition(posicao);
            onHotspotClick && onHotspotClick(equipamento);
          }}
          onMouseEnter={() => setHoveredPosition(posicao)}
          onMouseLeave={() => setHoveredPosition(null)}
        >
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <div className="HotspotAnnotation">{posicao}</div>
            {((selectedPosition === posicao) || hoveredPosition === posicao) && (() => {
              const info = getInfoForLayer(4); return (
                <div
                  className="HotspotInfo"
                  style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: 0,
                    transform: 'translateY(-4px)',
                    background: 'rgba(0,0,0,0.55)',
                    color: '#fff',
                    border: `1px solid ${info.borderColor}`,
                    borderRadius: 4,
                    padding: '2px 4px',
                    fontSize: 8,
                    lineHeight: 1.1,
                    whiteSpace: 'nowrap',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                    pointerEvents: 'none',
                  }}
                  title={info.title}
                >
                  <span>{info.text}</span>
                </div>
              );
            })()}
          </div>
        </button>
      );
    });

    return elements;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipamentos?.length, equipamentoSelecionado?._id, medicoes, getSensoresDoEquipamento, modelVariant, hoveredPosition, selectedLayer, selectedPosition]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* Seletor de modelo (Frente/Traseira) */}
      <div
        style={{
          position: 'absolute',
          top: isFullscreen ? 16 : 8,
          left: isFullscreen ? 16 : 8,
          zIndex: 20,
          background: 'rgba(255,255,255,0.92)',
          borderRadius: 10,
          padding: isFullscreen ? 6 : 4,
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.25)'
        }}
        role="group"
        aria-label="Selecionar vista do modelo"
      >
        <button
          type="button"
          onClick={() => setModelVariant('frente')}
          title="Ver modelo - Frente"
          style={{
            fontSize: isFullscreen ? 12 : 11,
            padding: '6px 10px',
            borderRadius: '8px',
            border: modelVariant === 'frente' ? '1px solid #8fad15' : '1px solid rgba(0,0,0,0.15)',
            background: modelVariant === 'frente' ? 'rgba(143,173,21,0.12)' : 'white',
            color: '#1c4220',
            cursor: 'pointer',
            marginRight: 6
          }}
        >
          Frente
        </button>
        <button
          type="button"
          onClick={() => setModelVariant('traseira')}
          title="Ver modelo - Traseira"
          style={{
            fontSize: isFullscreen ? 12 : 11,
            padding: '6px 10px',
            borderRadius: '8px',
            border: modelVariant === 'traseira' ? '1px solid #4dabf7' : '1px solid rgba(0,0,0,0.15)',
            background: modelVariant === 'traseira' ? 'rgba(77,171,247,0.12)' : 'white',
            color: '#1c4220',
            cursor: 'pointer'
          }}
        >
          Atrás
        </button>
      </div>
      {/* QR code sutil para abrir a página 3D nativa */}
      <div
        style={{
          position: 'absolute',
          top: isFullscreen ? 16 : 8,
          right: isFullscreen ? 16 : 8,
          zIndex: 20,
          opacity: 0.75,
          transition: 'opacity 0.2s ease-in-out',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = 1; }}
        onMouseLeave={(e) => { e.currentTarget.style.opacity = 0.75; }}
        aria-hidden={false}
      >
        <a
          href={target3DUrl}
          target="_blank"
          rel="noopener noreferrer"
          title="Abrir visualização 3D em nova aba"
          style={{
            display: 'inline-block',
            background: 'rgba(255,255,255,0.9)',
            borderRadius: 10,
            padding: isFullscreen ? 6 : 4,
            border: '1px solid rgba(0,0,0,0.08)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
          }}
        >
          <img
            src={qrImageUrl}
            alt="QR para visualização 3D"
            style={{
              display: 'block',
              width: isFullscreen ? 80 : 60,
              height: 'auto',
              borderRadius: 6,
            }}
          />

        </a>
      </div>

      {/* Card fixo acima do hotspot selecionado */}
      {/* {   cardData && (
        <div
          style={{
            position: 'absolute',
            left: 8,
            right: 8,
            top: 8,
            transform: 'none',
            backgroundColor: 'rgba(0, 0, 0, 0.68)',
            color: 'white',
            padding: '8px',
            borderRadius: '8px',
            fontSize: '11px',
            zIndex: 1000,
            pointerEvents: 'auto',
            boxShadow: '0 6px 18px rgba(0, 0, 0, 0.35)',
            border: '1px solid rgba(143, 173, 21, 0.5)',
            width: 'auto',
            backdropFilter: 'blur(8px)',
            opacity: 1,
            transition: 'opacity 0.2s ease-in-out',
            animation: 'tooltipFadeIn 0.2s ease-out',
            display: 'flex',
            gap: '8px',
            textShadow: '0 1px 2px rgba(0,0,0,0.6)'
          }}
        >

          <div style={{ flex: '0 0 130px', minWidth: 130 }}>
            <div style={{ fontWeight: '600', marginBottom: '6px', color: '#cfe08a', fontSize: '12px', lineHeight: 1.1 }}>
              Pos {cardData.posicao} • {cardData.equipamento.serialnumber}
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '4px' }}>
              <span style={{ fontSize: '11px', color: '#bbb' }}>
                {cardData.posicao <= 10 ? '🔥 Entrada' : '🌾 Saída'}
              </span>
              <span style={{ fontSize: '12px', color: cardData.equipamento.status === 'alerta' ? '#ff6b6b' : '#69db7c' }}>
                {cardData.equipamento.status === 'alerta' ? '⚠️' : '✅'}
              </span>
            </div>
          </div>

          {(() => {
            const sensoresEquipamento = getSensoresDoEquipamento(cardData.equipamento)
              .filter(sensor => sensor.ta !== -99 && sensor.tg !== -99 && sensor.ur !== -99)
              .reverse();

            if (sensoresEquipamento.length === 0) {
              return (
                <div style={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#666',
                  padding: '4px 0'
                }}>
                  <div style={{ fontSize: '10px', textAlign: 'center' }}>
                    📡 Sem dados de sensores
                  </div>
                </div>
              );
            }

            return (
              <div style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                paddingLeft: '2px'
              }}>
                {sensoresEquipamento.map((sensor) => {
                  const temFalha = sensor.ta === -88 || sensor.tg === -88 || sensor.ur === -88;
                  const statusSensor = temFalha ? 'falha' : sensor.status;
                  let borderColor = '#69db7c';
                  if (!temFalha && sensor.ta !== null && sensor.ta !== undefined) {
                    const temp = sensor.ta;
                    if (temp > 90) borderColor = '#ff6b6b';
                    else if (temp > 60) borderColor = '#ffd43b';
                    else if (temp < 40) borderColor = '#4dabf7';
                  } else if (temFalha) {
                    borderColor = '#dc3545';
                  }

                  return (
                    <div
                      key={sensor.id}
                      style={{
                        border: `1px solid ${borderColor}`,
                        borderRadius: '6px',
                        boxShadow: `0 1px 4px ${borderColor}33`,
                        padding: '4px 6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: 'rgba(255,255,255,0.03)'
                      }}
                      title={temFalha ? 'Falha detectada' : `TA ${sensor.ta}°C | TG ${sensor.tg}°C | UR ${sensor.ur}%`}
                    >
                      <span style={{ fontSize: '11px', color: '#e0e0e0', whiteSpace: 'nowrap' }}>
                        <strong style={{ color: '#8fad15' }}>S{sensor.id}</strong> • {temFalha ? (
                          <span style={{ color: '#dc3545', fontWeight: 'bold' }}>Falha</span>
                        ) : (
                          <span>TA {sensor.ta}° | TG {sensor.tg}° | UR {sensor.ur}%</span>
                        )}
                      </span>
                      <span style={{ fontSize: '12px' }}>
                        {temFalha ? '🔴' : statusSensor === 'alerta' ? '🔴' : '🟢'}
                      </span>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}
       */}

      {/* Container do modelo 3D */}
      <div
        ref={containerRef}
        style={{
          width: '100%',
          height: '100%',
          minHeight: isFullscreen ? '100vh' : 320,
          aspectRatio: isFullscreen ? 'auto' : '1/1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: loading || !isLoaded ? 0.9 : 1,
          transition: 'opacity 0.3s, filter 0.3s',
          perspective: '1000px'
        }}
        aria-label={alt}
      >
        {getDisplaySrc().endsWith('.glb') ? (
          <model-viewer
            key={`model-${modelVariant}`}
            ref={modelViewerRef}
            alt={alt}
            src={getDisplaySrc()}
            camera-controls="true"
            tone-mapping="neutral"
            shadow-intensity="1"
            shadow-softness="1"
            camera-orbit={modelVariant === 'traseira' ? (isFullscreen ? "260deg 60deg 75488m" : "260deg 60deg 75488m") : (isFullscreen ? "115deg 70deg 120m" : "115deg 70deg 120m")}
            exposure="1"
            loading="eager"
            reveal="auto"
            style={{
              width: '100%',
              height: '100%',
              background: 'linear-gradient(to right, rgba(28, 66, 32, 0.95), rgba(143, 173, 21, 0.9))',
              borderRadius: isFullscreen ? '0px' : '12px',
              minHeight: isFullscreen ? '100vh' : 'auto'
            }}
          >
            {/* Hotspots dinâmicos baseados nos equipamentos */}
            {hotspots}
          </model-viewer>
        ) : (
          <FallbackMessage onSwitch={() => { /* noop: fallback */ }} />
        )}
      </div>
    </div>
  );
};

export default React.memo(ThreeImageTiltSimple);
